import { useUserStore } from '@/store/userStore'
import log from '@/utils/useLogger'
import { fetchAuthSession } from 'aws-amplify/auth'
import { storeToRefs } from 'pinia'
import { emitEvent } from '@/utils/EventBus'
import { ref } from 'vue'

const isRefreshing = ref(false)
export const useTokenManager = () => {
  const { userDetails, token, isLoggedIn } = storeToRefs(useUserStore())
  const TOKEN_BUFFER = 5 * 60 * 1000

  const isTokenExpired = (): boolean => {
    if (!userDetails.value?.exp) return true
    const expirationTime = userDetails.value.exp * 1000
    const currentTime = Date.now()
    return expirationTime - currentTime < TOKEN_BUFFER
  }

  const getValidToken = async (force: boolean = false): Promise<string | null> => {
    if (!isLoggedIn.value || !token.value) return null

    // Refresh if forced OR expired AND not currently refreshing
    if ((force || isTokenExpired()) && !isRefreshing.value) {
      try {
        isRefreshing.value = true
        log.warn('Refreshing ID token...')

        const session = await fetchAuthSession({ forceRefresh: true })
        const idToken = session.tokens?.idToken

        if (!idToken) {
          log.error('No ID token found during refresh')
          emitEvent('sessionExpired', '')
          return null
        }

        token.value = idToken.toString()
        userDetails.value = idToken.payload as Record<string, any>

        log.info('Token refreshed successfully')
        return token.value
      } catch (err) {
        log.error('Token refresh failed', err)
        emitEvent('sessionExpired', '')
        return null
      } finally {
        isRefreshing.value = false // ALWAYS reset!
      }
    }

    return token.value
  }

  return { getValidToken, isTokenExpired }
}
